import { Link, Navigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { teamProgress, teammateFieldCount } from '@gg/shared';
import { Roster, TeammateForm } from '@/components/teams';
import { BackButton, Badge, buttonClasses, Card, Eyebrow } from '@/components/ui';
import { localName, membersLabel } from '@/lib/formats';
import { useMe, useTeam } from '@/lib/queries';

/**
 * /me/teams/:id — the captain manages the team (P-20 step 6): roster with resend / replace,
 * and the teammate fields for the seats left. A joined member sees the roster only.
 */
export function Team() {
  const { t } = useTranslation();
  const { id } = useParams();
  const me = useMe();
  const team = useTeam(me.data ? id : undefined);

  if (me.isLoading) return <p className="text-center text-ink-muted">…</p>;
  if (me.isError || !me.data) return <Navigate to="/login" replace />;
  if (team.isError) return <Navigate to="/me" replace />;
  if (!team.data) return <p className="text-center text-ink-muted">…</p>;

  const data = team.data;
  const progress = teamProgress(
    { teamSizeMin: data.sizeMin, teamSizeMax: data.sizeMax },
    data.members,
  );
  const fields =
    data.isCaptain && data.status !== 'cancelled' ? teammateFieldCount(progress.seatsLeft) : 0;

  return (
    <section className="flex flex-1 flex-col gap-5">
      <div className="flex items-center gap-3">
        <BackButton to="/me" label={t('teams.back')} />
        <Eyebrow>{localName(data.formatName, data.formatNameEn)}</Eyebrow>
      </div>

      <div className="flex flex-col gap-1">
        <h1 className="m-0 font-display text-section-lg font-bold">
          {t('teams.title', { name: data.name })}
        </h1>
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
          {membersLabel(t, progress.joined, data.sizeMax, data.sizeMin)}
          {data.status === 'complete' && <Badge tone="success">{t('teams.complete')}</Badge>}
          {data.status === 'cancelled' && <Badge>{t('teams.cancelled')}</Badge>}
        </p>
      </div>

      <Card>
        <Roster team={data} actions />
      </Card>

      {fields > 0 ? (
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 font-display text-section font-bold">{t('teams.addTitle')}</h2>
            <p className="m-0 text-sm leading-snug text-ink-muted">{t('teams.addSubtitle')}</p>
          </div>
          <TeammateForm
            key={`${data.id}-${fields}`}
            teamId={data.id}
            fields={fields}
            maxFields={progress.seatsLeft}
            draftKey={`team:${data.id}`}
          />
        </div>
      ) : (
        <div className="mt-auto flex flex-col gap-3 pb-4 text-center">
          <Link to="/me" className={buttonClasses('secondary', 'md')}>
            {t('teams.back')}
          </Link>
          <span className="text-xs text-ink-subtle">{t('teams.support')}</span>
        </div>
      )}
    </section>
  );
}
